import { IGroupListItemDef } from "@components/configurationList";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef,
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportRowDef, IReportVariantDef, ISmartReportTableRow } from "@components/smart/smartTable";
import { IFormatOptions } from "@odata/OData.utils";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import i18next from "i18next";

import { FieldType, RowType, Sort } from "../../../enums";
import { TRecordType, TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { ROUTE_ACCOUNT_ANALYSIS } from "../../../routes";
import { formatCurrency } from "../../../types/Currency";
import { AccountAnalysisProps } from "../accountAnalysis/AccountAnalysisDef";
import {
    CommonReportProps,
    documentNumberOursOverrideFormatterWithPaymentDocs,
    getDocumentTypeFilterDef, getReportLevelOfDetailDef, ReportLevelOfDetail,
} from "../CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs,
} from "../customFilterComponents/ComposedDateRange";
import {
    getLabelDrilldownFilters,
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportConfigGroup,
    TGetReportConfigListItemOverride,
    TimeAggregationFunction,
    TReportColumnOverrides,
    TReportConfigListItemOverride,
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";
import {
    buildAccountFilter,
    buildSideFilter,
    classifyRow,
    getLabelParameterDrilldownFilters,
    resolveDateRangeCustomValue,
    transformLabelsHierarchyFilter,
} from "./ExpensesAndRevenue.utils";

export const ExpensesAndRevenueProps = {
    levelOfDetail: BindingContext.localContext("LevelOfDetail"),
    labels: BindingContext.localContext("Labels"),
    taxApplicability: BindingContext.localContext("TaxApplicability"),
};

export enum ExpensesAndRevenueVariant {
    Evala = "-185",
}

const REQUIRED_AGGREGATIONS = new Set([
    "JournalEntry_CreditAmount",
    "JournalEntry_DebitAmount",
    "JournalEntry_Amount_Difference",
]);
const DEFAULT_DOCUMENTS_COLUMNS = new Set([
    "JournalEntry_DateAccountingTransaction",
    "Document_NumberOurs",
    "EntityBusinessPartner_Name",
    "DocumentItem_Description",
]);

export const expensesAndRevenueConfigListItemsOverride: TGetReportConfigListItemOverride = (
    item: IGroupListItemDef
): TReportConfigListItemOverride => {
    const id = item.id as string;

    if (REQUIRED_AGGREGATIONS.has(id)) {
        return {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            group: ReportConfigGroup.Aggregations,
        };
    }

    if (DEFAULT_DOCUMENTS_COLUMNS.has(id)) {
        return { isCopyOnly: false };
    }

    return item;
};

const getCommonPlaceholderReportHierarchy = (): IReportHierarchy => ({
    Aggregate: true,
    Groups: [
        {
            ColumnAlias: "AccountType_Name",
        },
        {
            ColumnAlias: "Account_Synthetic",
        },
    ],
    Columns: [],
    Aggregations: [
        {
            ColumnAlias: "JournalEntry_DebitAmount",
            AggregationFunction: NumberAggregationFunction.Sum,
        },
        {
            ColumnAlias: "JournalEntry_CreditAmount",
            AggregationFunction: NumberAggregationFunction.Sum,
        },
        {
            ColumnAlias: "JournalEntry_Amount_Difference",
            AggregationFunction: NumberAggregationFunction.Sum,
        },
    ],
});

export const getSyntheticLevelReportHierarchyPlaceholder = (): IReportHierarchy => ({
    ...getCommonPlaceholderReportHierarchy(),
    Columns: [...getCommonPlaceholderReportHierarchy().Columns],
    Aggregations: [...getCommonPlaceholderReportHierarchy().Aggregations],
});

export const getAnalyticLevelReportHierarchyPlaceholder = (): IReportHierarchy => ({
    ...getCommonPlaceholderReportHierarchy(),
    Groups: [
        ...getCommonPlaceholderReportHierarchy().Groups,
        {
            ColumnAlias: "Account_Analytic",
        },
    ],
    Aggregations: [...getCommonPlaceholderReportHierarchy().Aggregations],
});

export const getDocumentsLevelReportHierarchyPlaceholder = (): IReportHierarchy => ({
    ...getCommonPlaceholderReportHierarchy(),
    Groups: [
        ...getCommonPlaceholderReportHierarchy().Groups,
        {
            ColumnAlias: "Account_Analytic",
        },
    ],
    Columns: [
        ...getCommonPlaceholderReportHierarchy().Columns,
        {
            ColumnAlias: "JournalEntry_DateAccountingTransaction",
            AggregationFunction: TimeAggregationFunction.Day,
        },
        {
            ColumnAlias: "Document_NumberOurs",
        },
        {
            ColumnAlias: "EntityBusinessPartner_Name",
        },
        {
            ColumnAlias: "DocumentItem_Description",
        },
    ],
    Aggregations: [...getCommonPlaceholderReportHierarchy().Aggregations],
});

export const getReportHierarchyByLevelOfDetail = (levelOfDetail: ReportLevelOfDetail): IReportHierarchy => {
    switch (levelOfDetail) {
        case ReportLevelOfDetail.DocumentsLevel:
            return getDocumentsLevelReportHierarchyPlaceholder();
        case ReportLevelOfDetail.AnalyticLevel:
            return getAnalyticLevelReportHierarchyPlaceholder();
        case ReportLevelOfDetail.SyntheticLevel:
        default:
            return getSyntheticLevelReportHierarchyPlaceholder();
    }
};

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.ExpensesAndRevenue;
    const title = i18next.t("Reporting:ExpensesAndRevenue.Title");
    const path = "ExpensesAndRevenue";
    const initialSortBy = [{ id: "Account_Number", sort: Sort.Asc }];
    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.dateRange,
                CommonReportProps.dateRangeCustomValue,
                ExpensesAndRevenueProps.levelOfDetail,
                ExpensesAndRevenueProps.taxApplicability,
                ExpensesAndRevenueProps.labels,
            ],
            filterDefinition: {
                ...getComposedDateRangeFieldsDefs(tableId),
                [ExpensesAndRevenueProps.labels]: {
                    type: FieldType.LabelSelect,
                    label: i18next.t("Reporting:ExpensesAndRevenue.Labels"),
                    filter: {
                        transformFilterValue: transformLabelsHierarchyFilter
                    },
                    fieldSettings: {
                        oneItemPerHierarchy: false,
                        addEmptyItem: false,
                        addEmptyValuesForHierarchies: true,
                        combinedLabelId: true,
                        alwaysIncludeHierarchyName: true
                    },
                },
                [ExpensesAndRevenueProps.taxApplicability]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:ExpensesAndRevenue.TaxApplicability"),
                    filter: {
                        transformFilterValue: (value: string) => (value === "All" ? null : value),
                    },
                    fieldSettings: {
                        items: [
                            {
                                label: i18next.t("Reporting:ExpensesAndRevenue.TaxApplicability_All"),
                                id: null,
                            },
                            {
                                label: i18next.t("Reporting:ExpensesAndRevenue.TaxApplicability_Applicable"),
                                id: "Applicable",
                            },
                            {
                                label: i18next.t("Reporting:ExpensesAndRevenue.TaxApplicability_NotApplicable"),
                                id: "NotApplicable",
                            },
                        ],
                    },
                },
                [ExpensesAndRevenueProps.levelOfDetail]: getReportLevelOfDetailDef()
            },
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {
                [BindingContext.localContext("DocumentType_Name")]: getDocumentTypeFilterDef(
                    "JournalEntry_DocumentTypeCode"
                ),
            },
        },
    ];
    const rowFormatter = (rowDef: IReportRowDef): Partial<ISmartReportTableRow> => {
        const isGrandTotal = rowDef.Value.GroupColumnAlias === "AccountType_Name" && !rowDef.Value.AccountType_Name;

        if (isGrandTotal) {
            return {
                isHighlighted: true,
                type: RowType.Aggregation,
                isBold: true,
                labelOverride: i18next.t("Reporting:ExpensesAndRevenue.GrandTotalLabel") as string,
            };
        }

        return {};
    };

    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage);
    };

    const parameters: string[] = [
        CommonReportProps.dateRange,
        CommonReportProps.dateRangeCustomValue,
        ExpensesAndRevenueProps.levelOfDetail,
        ExpensesAndRevenueProps.labels,
        ExpensesAndRevenueProps.taxApplicability,
    ];
    const defaultReportVariants: TRecordType<IReportVariantDef> = {
        [ExpensesAndRevenueVariant.Evala]: {
            ReportHierarchy: getReportHierarchyByLevelOfDetail(ReportLevelOfDetail.AnalyticLevel),
        },
    };
    const defaultReportHierarchy = defaultReportVariants[ExpensesAndRevenueVariant.Evala].ReportHierarchy;

    const drillDownColumns = [
        "JournalEntry_CreditAmount",
        "JournalEntry_DebitAmount",
        "JournalEntry_Amount_Difference",
    ];

    const columnOverrides: TReportColumnOverrides = (columnId: string) => {
        if (columnId === "AccountType_Name") {
            return { label: i18next.t("Reporting:ExpensesAndRevenue.FirstColumnLabel") };
        }

        if (columnId === "Document_NumberOurs") {
            return {
                formatter: documentNumberOursOverrideFormatterWithPaymentDocs,
            };
        }

        const drillDownColumn = drillDownColumns.find((col) => columnId.startsWith(col));
        if (!drillDownColumn) {
            return null;
        }

        return {
            label: i18next.t(`Reporting:ExpensesAndRevenue.${drillDownColumn}`) as string,
            formatter: (val: TValue, args: IFormatOptions) => {
                const storage = args.storage as ReportStorage;
                const isDebitColumn = columnId.startsWith("JournalEntry_DebitAmount");
                const isCreditColumn = columnId.startsWith("JournalEntry_CreditAmount");
                const isDifferenceColumn = columnId.startsWith("JournalEntry_Amount_Difference");

                const groupColumnAlias = args.entity?.GroupColumnAlias as string;
                const rowKind = classifyRow(args.entity, groupColumnAlias);

                const formattedVal = formatCurrency(
                    isDifferenceColumn && rowKind !== "grandTotal" ? Math.abs(val as number) : (val as number),
                    getCompanyCurrency(args.context)
                );

                const debitDrilldownEnabled = (args.entity?.["DebitAmountDrilldownEnabled"] as boolean | undefined);
                const creditDrilldownEnabled = (args.entity?.["CreditAmountDrilldownEnabled"] as boolean | undefined);
                const showZeroDrilldown = isDebitColumn ? debitDrilldownEnabled : isCreditColumn ? creditDrilldownEnabled : false;

                if ((!val || val === 0) && !showZeroDrilldown) {
                    return formattedVal;
                }

                const isDrillable = rowKind !== "other" && (rowKind !== "grandTotal" || !isDifferenceColumn);

                if (!isDrillable) {
                    return formattedVal;
                }

                const accountFilter = buildAccountFilter(
                    args.entity,
                    rowKind === "sectionTotal",
                    rowKind === "grandTotal",
                    storage
                );
                if (accountFilter === null) {
                    return formattedVal;
                }

                return getTableDrillDownLink(formattedVal, {
                    route: ROUTE_ACCOUNT_ANALYSIS,
                    context: args.context,
                    storage: args.storage,
                    filters: {
                        [CommonReportProps.dateRange]: CUSTOM_DATE_RANGE_ID,
                        [CommonReportProps.dateRangeCustomValue]: resolveDateRangeCustomValue(storage),
                        ...accountFilter,
                        [AccountAnalysisProps.sumAmounts]: !isDebitColumn && !isCreditColumn,
                        ...buildSideFilter(isDebitColumn, isCreditColumn),
                        ...(storage.data.entity[ExpensesAndRevenueProps.taxApplicability]
                            ? {
                                  [AccountAnalysisProps.taxApplicability]:
                                      storage.data.entity[ExpensesAndRevenueProps.taxApplicability],
                              }
                            : {}),
                        ...getLabelParameterDrilldownFilters(storage, ExpensesAndRevenueProps.labels),
                        ...getLabelDrilldownFilters(args.entity),
                    },
                });
            },
        };
    };

    return {
        title,
        path,
        id: tableId,
        initialSortBy,
        filterBarDef,
        onBeforeLoad,
        columnOverrides,
        parameters,
        defaultReportHierarchy,
        defaultReportVariants,
        rowFormatter,
        configListItemsOverride: expensesAndRevenueConfigListItemsOverride,
        disableAggregateButton: true,
    };
};

setReportDef(ReportId.ExpensesAndRevenue, getDefinition);
